import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import type { FilterCondition } from "epsm-api-types";
import type { ScreenerFilterData } from "epsm-api-types/models";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import type { SingleValue } from "react-select";
import { ClipLoader } from "react-spinners";
import {
	type FilterDataContextProps,
	useFilterData,
} from "@/context/FilterDataContext";
import { useWidgetView } from "@/context/WidgetViewContext";
import type { Option } from "@/interfaces/Option";
import { getUnixTime } from "@/lib/utils";
import { getFirstNestedSubfield } from "../../lib/helpers/getFirstNestedSubfield";
import { isRelativeDate } from "../../lib/helpers/isRelativeDate";
import { DateFilters } from "./DateFilters";
import { ListFilters } from "./ListFilters";
import { NumberFilters } from "./NumberFilters";
import { StringFilters } from "./StringFilters";

export const Filters = ({
	width,
	refetch,
}: {
	width?: number;
	refetch: UseInfiniteQueryResult["refetch"];
}) => {
	const { selectedColumn } = useWidgetView();
	const {
		executeFilterData,
		isLoading,
		filterData,
		resultsRef,
		setValue,
		setListOptions,
		getOptions,
		setField,
		isDate,
		selectedSubField,
		setSelectedSubField,
		isResultSelectedRef,
	} = useFilterData() as {
		resultsRef: React.RefObject<
			Array<{
				result: Array<{
					name: string;
					condition: FilterCondition;
					value: unknown;
				}>;
			}>
		>;
	} & FilterDataContextProps;

	const prevSelectedSubFieldRef = useRef<SingleValue<Option>>(null);

	const [height, setHeight] = useState(0);
	const divRef = useRef(null);

	useEffect(() => {
		const handleResize = (entries: ResizeObserverEntry[]) => {
			for (const entry of entries) {
				setHeight(entry.contentRect.height);
			}
		};

		const resizeObserver = new ResizeObserver(handleResize);

		if (divRef.current) {
			resizeObserver.observe(divRef.current);
		}

		return () => {
			if (divRef.current) {
				resizeObserver.unobserve(divRef.current);
			}
		};
	}, []);

	const hasNull = useCallback((data: ScreenerFilterData | null) => {
		if (!data) return null;

		return data.items.some((item) => item === null);
	}, []);

	const handleFilterData = useCallback(
		(data: ScreenerFilterData | null) => {
			const formattedItems =
				data?.items.map((item) =>
					isDate() ? String(getUnixTime(item as string)) : String(item),
				) || [];

			hasNull(data)
				? setValue({ min: "", max: "" })
				: setValue({
						min: formattedItems[0],
						max: formattedItems[formattedItems.length - 1],
					});
		},
		[setValue, isDate, hasNull],
	);

	const getValue = useCallback(
		(value: unknown) => {
			if (isDate() && isRelativeDate(value)) {
				const daysInMs = Math.round(Number(value));
				return daysInMs < 0 ? `${daysInMs}` : `+${daysInMs}`;
			}

			return String(value);
		},
		[isDate],
	);

	useLayoutEffect(() => {
		if (!selectedColumn.Screener) return;

		const isSameOption = prevSelectedSubFieldRef.current === selectedSubField;

		const subField =
			isSameOption && selectedColumn.Screener.subFields?.length
				? getFirstNestedSubfield(selectedColumn.Screener.subFields)
				: ((selectedSubField?.value as string) ??
					getFirstNestedSubfield(selectedColumn.Screener.subFields!));

		const name = selectedColumn.Screener.name;

		const foundResult = resultsRef.current.find(
			(result) =>
				result.result[0].name === name ||
				result.result[0].name === `${name}.${subField}`,
		);

		if (foundResult) {
			if (selectedColumn.Screener.view === "list") {
				if (
					selectedColumn.Screener?.options &&
					selectedColumn.Screener.type === "str"
				) {
					const items = foundResult.result[0].value as unknown[];

					const options = items.map((item) => {
						const key = item as keyof typeof selectedColumn.Screener.options;

						return selectedColumn.Screener?.options?.[key]
							? { value: item, label: selectedColumn.Screener.options[key] }
							: getOptions(item, selectedColumn.Screener!);
					});

					setListOptions(options);
				} else {
					const options = (foundResult.result[0].value as unknown[]).map(
						(item) => getOptions(item, selectedColumn.Screener!),
					);
					setListOptions(options);
					executeFilterData({
						name: selectedColumn.Screener.name,
						subField,
					});
				}
			} else {
				switch (selectedColumn.Screener.type) {
					case "num":
					case "date": {
						if (foundResult.result.length === 2) {
							setValue({
								min: getValue(foundResult.result[0].value),
								max: getValue(foundResult.result[1].value),
							});
						} else {
							const isMin = foundResult.result[0].condition.startsWith("g");

							setValue({
								min: isMin ? getValue(foundResult.result[0].value) : "",
								max: isMin ? "" : getValue(foundResult.result[0].value),
							});
						}
						executeFilterData({
							name: selectedColumn.Screener.name,
							subField,
						});
						break;
					}

					case "str":
						setField(foundResult.result[0].value as string);
						break;

					case "bool": {
						const rawValue = foundResult.result[0].value;
						const values = Array.isArray(rawValue) ? rawValue : [rawValue];

						const options = values.map((item) =>
							getOptions(item, selectedColumn.Screener!),
						);

						setListOptions(options);
						break;
					}
				}
			}
		} else {
			if (selectedColumn.Screener.view === "list") {
				if (
					selectedColumn.Screener?.options &&
					selectedColumn.Screener.type === "str"
				) {
					setListOptions([]);
				} else {
					setListOptions([]);
					executeFilterData({ name: selectedColumn.Screener.name });
				}
			} else {
				switch (selectedColumn.Screener.type) {
					case "num":
					case "date": {
						if (selectedColumn.Screener.subFields?.length && subField) {
							executeFilterData({
								name: selectedColumn.Screener.name,
								subField,
							}).then((data) => handleFilterData(data));
						} else {
							executeFilterData({
								name: selectedColumn.Screener.name,
							}).then((data) => handleFilterData(data));
						}
						break;
					}

					case "str":
						setField("");
						break;

					case "bool":
						setListOptions([]);
						break;
				}
			}
		}

		prevSelectedSubFieldRef.current = selectedSubField;
	}, [
		handleFilterData,
		getValue,
		selectedColumn.Screener,
		executeFilterData,
		resultsRef,
		setValue,
		setListOptions,
		getOptions,
		setField,
		selectedSubField,
	]);

	useEffect(() => {
		if (!selectedColumn.Screener) return;

		if (isResultSelectedRef.current) {
			setSelectedSubField(prevSelectedSubFieldRef.current);

			isResultSelectedRef.current = false;
		} else {
			setSelectedSubField(null);
		}
	}, [selectedColumn.Screener, setSelectedSubField, isResultSelectedRef]);

	const renderFilters = () => {
		if (selectedColumn.Screener) {
			if (selectedColumn.Screener.searchDisabled) return;

			if (selectedColumn.Screener.view === "list") {
				if (
					selectedColumn.Screener?.options &&
					selectedColumn.Screener.type === "str"
				) {
					return <ListFilters height={height} refetch={refetch} />;
				}
				return isLoading || !filterData ? (
					<ClipLoader className="flex self-center" size={30} color="#6a6a6a" />
				) : (
					<ListFilters height={height} refetch={refetch} />
				);
			}
			switch (selectedColumn.Screener.type) {
				case "num":
					return isLoading || !filterData ? (
						<ClipLoader
							className="flex self-center"
							size={30}
							color="#6a6a6a"
						/>
					) : !isLoading ? (
						<NumberFilters width={width} refetch={refetch} hasNull={hasNull} />
					) : null;
				case "date":
					return isLoading || !filterData ? (
						<ClipLoader
							className="flex self-center"
							size={30}
							color="#6a6a6a"
						/>
					) : !isLoading ? (
						<DateFilters width={width} refetch={refetch} hasNull={hasNull} />
					) : null;
				case "str":
					return <StringFilters refetch={refetch} />;
				case "bool":
					return <ListFilters height={height} refetch={refetch} />;
			}
		}

		return null;
	};

	return (
		<div ref={divRef} className="min-w-40 w-2/4 border-box rounded-sm">
			<h3 className="text-accent-foreground italic py-1 px-1.5">Filters</h3>

			<div className="flex justify-center px-1.5 overflow-y-scroll h-[calc(100%-36px)]">
				{renderFilters()}
			</div>
		</div>
	);
};
